import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { takeUntil } from 'rxjs';
import { TuiDialogContext } from '@taiga-ui/core';
import { injectContext } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { AlertService, OrganisationService } from '@src/core/services';
import { BaseResizableComponent } from '@src/app/modules/base';

import { LoyaltyProgramApiService } from '../../../services';
import { QR_CODE_IMAGE_WIDTH } from '../../constants';

import { LoyaltyProgramDiscountDialogData } from './types';

@Component({
  selector: 'lp-view-discount-dialog',
  templateUrl: './discount-dialog.component.html',
  styleUrls: ['./discount-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscountDialogComponent extends BaseResizableComponent {
  private readonly clipboard = inject(Clipboard);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly loyaltyProgramApiService = inject(LoyaltyProgramApiService);
  private readonly organisationService = inject(OrganisationService);
  private readonly context = injectContext<TuiDialogContext<void, LoyaltyProgramDiscountDialogData>>();

  readonly qrCodeImageWidth = QR_CODE_IMAGE_WIDTH;

  /** Признак использования unionId в запросах */
  private useUnionId = false;

  readonly barcodeOptions: JsBarcode.Options = {
    format: 'EAN13',
  };

  override ngOnInit(): void {
    super.ngOnInit();

    this.organisationService.currentParentOrganisation$.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      this.setUseUnionId();
    });
  }

  get discount() {
    return this.context.data;
  }

  get isQrCodeImage(): boolean {
    return !!this.discount?.discountQRCodeFileId;
  }

  get productId() {
    return this.discount.productId;
  }

  /** ID ассоциации внешней базы */
  get externalUnionId(): string | undefined {
    return this.useUnionId ? this.discount?.unionId : undefined;
  }

  /** Заполняем признак использования unionId */
  setUseUnionId() {
    this.useUnionId = this.discount?.unionId
      ? this.organisationService.currentParentOrganisation$.value?.id !== this.discount.unionId
      : false;
    this.cdr.markForCheck();
  }

  copyPromoCode(): void {
    if (this.clipboard.copy(this.discount?.code ?? '')) {
      this.alertService.success(
        this.translateService.instant('components.loyaltyDiscountDialog.alerts.successes.copyCode'),
      );
    }
  }

  saveRedirectLink(link: string) {
    if (!this.productId) return;

    this.loyaltyProgramApiService.saveRedirectLink(this.productId, link, this.externalUnionId);
  }

  onClose() {
    this.context.completeWith();
  }
}
